export type OrganizationType = 'agency' | 'client' | 'platform';
export type OrganizationStatus = 'draft' | 'pending_review' | 'active' | 'suspended';

export const capabilityValues = [
  'overview:view',
  'performance:view',
  'organizations:view',
  'organizations:manage',
  'talent:view',
  'talent:manage',
  'campaigns:view',
  'campaigns:manage',
  'venues:view',
  'venues:request',
  'venues:manage',
  'assignments:view',
  'assignments:manage',
  'attendance:review',
  'attendance:view',
  'attendance:submit',
  'products:view',
  'products:manage',
  'sales:view',
  'sales:review',
  'sales:import',
  'sales:submit',
  'shelf_evidence:view',
  'shelf_evidence:review',
  'shelf_evidence:submit',
  'stock:view',
  'stock:receive',
  'settings:view',
  'settings:manage',
  'users:view',
  'audit:view',
  'supervision:manage',
  'supervision:view',
  'supervision:execute',
  'merchandising:manage',
  'merchandising:view',
  'merchandising:execute',
  'surveys:view',
  'surveys:manage',
  'surveys:submit',
  'surveys:manual_entry',
] as const;

export type Capability = typeof capabilityValues[number];

export type MembershipRole =
  | 'platform_admin'
  | 'org_admin'
  | 'project_manager'
  | 'viewer'
  | 'supervisor'
  | 'merchandiser';

export interface Profile {
  id: string;
  fullName: string;
  email: string | null;
  emailVerified: boolean;
  consents: ConsentSummary;
  phone: string | null;
  avatar: string | null;
  cnic: string | null;
  dateOfBirth: string | null;
  isBrandAmbassador: boolean;
  isMerchandiser: boolean;
  merchandiserReference: string | null;
  baVerificationStatus?: 'draft' | 'pending' | 'verified' | 'rejected' | 'suspended' | null;
}

export interface ConsentStatus {
  version: string;
  url: string;
  required: boolean;
  accepted: boolean;
  recordedAt: string | null;
}

export interface ConsentSummary {
  terms: ConsentStatus;
  privacy: ConsentStatus;
  marketing: ConsentStatus;
}

export interface ConsentPolicyConfiguration {
  terms: Pick<ConsentStatus, 'version' | 'url' | 'required'>;
  privacy: Pick<ConsentStatus, 'version' | 'url' | 'required'>;
  marketing: Pick<ConsentStatus, 'version' | 'url' | 'required'>;
}

export interface Membership {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationType: OrganizationType;
  organizationLogo: string | null;
  organizationThemePrimaryColor?: string;
  organizationThemeAccentColor?: string;
  organizationUsesAgencyLogo?: boolean;
  organizationUsesAgencyName?: boolean;
  role: MembershipRole;
  capabilities: Capability[];
}

export interface UserDirectoryMembership {
  id: string;
  organization_id: string;
  organization_name: string;
  organization_type: OrganizationType;
  role: MembershipRole;
  is_active: boolean;
}

export interface UserDirectoryEntry {
  account_type_change?: {
    target: 'brand_ambassador' | 'organization';
    allowed: boolean;
    reason: string;
  };
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar: string | null;
  is_active: boolean;
  email_verified: boolean;
  is_brand_ambassador: boolean;
  is_merchandiser: boolean;
  is_platform_admin: boolean;
  ba_reference: string | null;
  merchandiser_reference: string | null;
  ba_verification_status: 'draft' | 'pending' | 'verified' | 'rejected' | 'suspended' | null;
  memberships: UserDirectoryMembership[];
  date_joined: string;
  last_seen_at: string | null;
}

export interface Workspace {
  id: string;
  name: string;
  type: OrganizationType | 'personal';
  roles: MembershipRole[];
  capabilities: Capability[];
  organizationId: string | null;
  logo: string | null;
  themePrimaryColor?: string;
  themeAccentColor?: string;
  usesAgencyLogo?: boolean;
  usesAgencyName?: boolean;
  isGlobal: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CursorPaginatedResponse<T> {
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface SupervisorOption {
  id: string;
  name: string;
}

export interface SupervisorLocationOption {
  id: string;
  campaign: string;
  campaign_name: string;
  venue_name: string;
  venue_city: string;
}

export interface SupervisionOptions {
  supervisors: SupervisorOption[];
  locations: SupervisorLocationOption[];
}

export interface MerchandiserOption {
  id: string;
  name: string;
}

export interface MerchandiserCampaignOption {
  id: string;
  name: string;
  client: string;
  client_name: string;
  starts_on: string;
  ends_on: string;
}

export interface MerchandiserLocationOption {
  id: string;
  campaign: string;
  venue_name: string;
  venue_city: string;
}

export interface MerchandiserBrandOption {
  id: string;
  name: string;
  client: string;
}

export interface MerchandisingOptions {
  merchandisers: MerchandiserOption[];
  campaigns: MerchandiserCampaignOption[];
  locations: MerchandiserLocationOption[];
  brands: MerchandiserBrandOption[];
}

export interface MerchandiserRouteStop {
  id: string;
  reference: string;
  location: string;
  venue: string;
  venue_name: string;
  venue_address: string;
  venue_city: string;
  sequence: number;
  planned_duration_minutes: number;
  instructions: string;
}

export interface MerchandiserRoute {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  campaign: string;
  campaign_name: string;
  merchandiser: string;
  merchandiser_name: string;
  name: string;
  schedule_type: 'recurring' | 'one_off';
  starts_on: string | null;
  ends_on: string | null;
  scheduled_on: string | null;
  weekdays: string[];
  planned_start_time: string;
  planned_end_time: string;
  instructions: string;
  status: 'active' | 'cancelled';
  cancellation_reason: string;
  brands: Array<{ id: string; name: string }>;
  stops: MerchandiserRouteStop[];
  created_at: string;
  updated_at: string;
}

export interface MerchandiserVisit {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  merchandiser: string;
  merchandiser_name: string;
  route: string;
  route_reference: string;
  route_name: string;
  stop: string;
  stop_sequence: number;
  campaign: string;
  campaign_name: string;
  location: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  scheduled_for: string;
  completed_at: string;
  notes: string;
  created_at: string;
}

export interface SupervisorStoreResponsibility {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  supervisor: string;
  supervisor_name: string;
  campaign: string;
  campaign_name: string;
  location: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  effective_from: string;
  effective_until: string | null;
  created_by_change: string;
  ended_by_change: string | null;
  created_at: string;
}

export interface SupervisorResponsibilityChange {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  effective_at: string;
  reason: string;
  changed_by_name: string;
  created_at: string;
}

export interface SupervisorResponsibilityTransferResponse {
  change: SupervisorResponsibilityChange;
  responsibilities: SupervisorStoreResponsibility[];
}

export interface SupervisorVisitEvent {
  id: string;
  reference: string;
  event_type: 'check_in' | 'check_out';
  captured_at: string;
  received_at: string;
  latitude: string;
  longitude: string;
  horizontal_accuracy_m: string;
  distance_from_site_m: string;
  location_captured_at: string | null;
  location_mocked: boolean | null;
  location_sample_count: number | null;
  location_max_drift_m: string | null;
  selfie: string | null;
  validation_status: 'accepted' | 'flagged' | 'rejected';
  validation_reason: string;
}

export interface SupervisorVisit {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  supervisor: string;
  supervisor_name: string;
  campaign: string;
  campaign_name: string;
  location: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  responsibility: string;
  status: 'rejected' | 'open' | 'completed';
  checked_in_at: string | null;
  checked_out_at: string | null;
  duration_seconds: number | null;
  report: {
    id: string;
    reference: string;
    review_status: 'pending' | 'approved' | 'rejected';
  };
  stock_counts: Array<{
    id: string;
    reference: string;
    captured_at: string;
    review_status: 'pending' | 'approved' | 'rejected';
  }>;
  events: SupervisorVisitEvent[];
  created_at: string;
}

export interface SupervisorVisitReportDecision {
  id: string;
  decision: 'approved' | 'rejected';
  note: string;
  reviewer: string;
  reviewer_name: string;
  reviewer_organization: string | null;
  reviewer_organization_name: string | null;
  created_at: string;
}

export interface SupervisorVisitReport {
  id: string;
  reference: string;
  visit: string;
  visit_reference: string;
  organization: string;
  organization_name: string;
  supervisor: string;
  supervisor_name: string;
  campaign: string;
  campaign_name: string;
  location: string;
  venue_name: string;
  venue_city: string;
  training_provided: boolean;
  training_topics: string;
  product_knowledge_tested: boolean;
  product_knowledge_result: 'not_tested' | 'passed' | 'needs_coaching';
  coaching_notes: string;
  shelf_checked: boolean;
  stock_count_verified: boolean;
  stock_observation: string;
  general_notes: string;
  complaints: Array<{
    id: string;
    source: 'brand_ambassador' | 'store_manager' | 'supervisor';
    category: 'product' | 'store' | 'brand_ambassador' | 'timing' | 'environment' | 'commute' | 'other';
    severity: 'low' | 'medium' | 'high';
    description: string;
    created_at: string;
  }>;
  photos: Array<{ id: string; photo: string; caption: string; created_at: string }>;
  review_status: 'pending' | 'approved' | 'rejected';
  can_decide: boolean;
  review_decisions: SupervisorVisitReportDecision[];
  created_at: string;
}

export type AuditOutcome = 'succeeded' | 'rejected' | 'failed';

export interface AuditEvent {
  id: string;
  recorded_at: string;
  actor: string | null;
  actor_label: string;
  action: string;
  outcome: AuditOutcome;
  entity_type: string;
  entity_public_id: string | null;
  entity_label: string;
  workspace: string | null;
  workspace_name: string | null;
  request_id: string;
  changes: Record<string, unknown>;
}

export interface Organization {
  id: string;
  type: OrganizationType;
  name: string;
  legal_name: string;
  short_name: string;
  slug: string;
  status: OrganizationStatus;
  logo: string | null;
  primary_contact_name: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  website: string;
  address: string;
  city: string;
  country: string;
  is_active: boolean;
  onboarding: {
    completed: number;
    total: number;
    percentage: number;
    is_complete: boolean;
    items: Array<{ key: string; label: string; complete: boolean }>;
  };
  created_at: string;
  updated_at: string;
}

export type AttendanceCheckinExportColumn = 'serial_number' | 'brand_ambassador' | 'store' | 'attendance_status'
  | 'captured_at' | 'gps_evidence' | 'validation' | 'review' | 'selfie';

export interface OrganizationSettings {
  id: string;
  organization_name: string;
  organization_type: OrganizationType;
  organization_logo: string | null;
  timezone: string;
  reporting_currency: 'PKR';
  attendance_checkin_export_columns: AttendanceCheckinExportColumn[];
  theme_primary_color: string;
  theme_accent_color: string;
  use_agency_logo: boolean;
  use_agency_name: boolean;
  default_allowed_radius_m: number;
  default_checkin_open_minutes: number;
  default_late_after_minutes: number;
  default_checkout_grace_minutes: number;
  updated_at: string;
}

export interface ClientAgencyRelationship {
  id: string;
  agency: string;
  agency_name: string;
  client: string;
  client_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMembership {
  id: string;
  organization: string;
  organization_name: string;
  organization_type: OrganizationType;
  user: string;
  email: string;
  full_name: string;
  role: MembershipRole;
  is_active: boolean;
  last_seen_at: string | null;
  created_at: string;
  updated_at: string;
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface OrganizationInvitation {
  id: string;
  organization: string;
  organization_name: string;
  organization_type: OrganizationType;
  email: string;
  role: MembershipRole;
  status: InvitationStatus;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  last_sent_at: string;
  created_at: string;
  accept_url: string | null;
}

export interface TalentProfile {
  id: string;
  reference: string;
  user_id: string;
  email: string | null;
  phone: string | null;
  display_name: string;
  cnic: string | null;
  date_of_birth: string | null;
  city: string;
  languages: string[];
  skills: string[];
  categories: string[];
  bio: string;
  avatar: string | null;
  is_available: boolean;
  is_searchable: boolean;
  is_assignment_eligible: boolean;
  verification_status: 'draft' | 'pending' | 'verified' | 'rejected' | 'suspended';
  onboarding: {
    completed: number;
    total: number;
    percentage: number;
    is_complete: boolean;
    can_submit: boolean;
    items: Array<{ key: string; label: string; complete: boolean }>;
  };
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string;
  created_at: string;
  updated_at: string;
}

export interface Campaign {
  can_import_sales?: boolean;
  id: string;
  reference: string;
  owner_organization: string;
  owner_name: string;
  client: string;
  client_name: string;
  operating_agency: string | null;
  operating_agency_name: string | null;
  operational_defaults: {
    timezone: string;
    reporting_currency: 'PKR';
    default_allowed_radius_m: number;
    default_checkin_open_minutes: number;
    default_late_after_minutes: number;
    default_checkout_grace_minutes: number;
  };
  schedule_rule_templates: ScheduleRuleTemplate[];
  name: string;
  description: string;
  starts_on: string;
  ends_on: string;
  status: 'draft' | 'staffing' | 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface ScheduleRuleTemplate {
  id: string;
  rule_number: number;
  name: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  start_time: string;
  end_time: string;
}

export type ActivitySiteKind =
  | 'university'
  | 'college'
  | 'school'
  | 'hospital_clinic'
  | 'corporate_office'
  | 'mall_activation_area'
  | 'event_venue'
  | 'community_centre'
  | 'outdoor_public_space'
  | 'other';

export interface CampaignLocation {
  id: string;
  campaign: string;
  campaign_name: string;
  venue: string | null;
  activity_site: string | null;
  site_type: 'store' | ActivitySiteKind;
  name: string;
  address: string;
  city: string;
  country: string;
  latitude: string;
  longitude: string;
  allowed_radius_m: number;
  timezone: string;
  venue_status: 'open' | 'closed' | 'merged' | 'unavailable';
  created_at: string;
  updated_at: string;
}

export interface CampaignActivitySite {
  id: string;
  campaign: string;
  campaign_name: string;
  catalogue_site: string | null;
  kind: ActivitySiteKind;
  name: string;
  campus_name: string;
  address: string;
  area: string;
  city: string;
  province: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  contact_name: string;
  contact_phone: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActivitySite {
  id: string;
  kind: ActivitySiteKind;
  name: string;
  campus_name: string;
  address: string;
  area: string;
  city: string;
  province: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActivityReportCertification {
  id: string;
  reference: string;
  revision: number;
  supersedes_id: string | null;
  snapshot: ActivityReportSnapshot;
  data_fingerprint: string;
  certified_by_id: string;
  certified_by_name: string;
  certification_role: 'lead' | 'backup';
  certified_at: string;
  note: string;
}

export interface ActivitySessionTotals {
  student_interceptions: number;
  samples: number;
  doctor_consultations: number;
  completed_surveys: number;
  sales_units: number;
  sales_value: string | null;
}

export interface ActivityReportSnapshot {
  session_id: string;
  session_reference: string;
  campaign_id: string;
  campaign_name: string;
  activity_site_id: string;
  activity_site_name: string;
  city: string;
  starts_at: string;
  ends_at: string;
  totals: ActivitySessionTotals;
  sales_by_sku: Array<{
    product_id: string;
    sku: string;
    product_name: string;
    quantity: number;
    value: string | null;
  }>;
  survey_answer_counts: Record<string, Record<string, number>>;
  attendance: Array<{
    reference: string;
    ba_id: string;
    ba_name: string;
    event_type: 'check_in' | 'check_out';
    captured_at: string;
    validation_status: string;
  }>;
}

export interface ActivityReportProvenance {
  site_contact: {
    name: string;
    phone: string;
  };
  team: Array<{
    assignment_id: string;
    assignment_reference: string;
    ba_id: string;
    ba_name: string;
    role: 'lead' | 'backup' | 'team';
    check_in: {
      reference: string;
      captured_at: string;
      validation_status: string;
    } | null;
    check_out: {
      reference: string;
      captured_at: string;
      validation_status: string;
    } | null;
  }>;
  counter_entries: Array<{
    reference: string;
    ba_id: string;
    ba_name: string;
    metric: 'student_interceptions' | 'samples' | 'doctor_consultations';
    quantity: number;
    captured_at: string;
    received_at: string;
    note: string;
  }>;
  readiness: {
    team_member_count: number;
    checked_in_count: number;
    checked_out_count: number;
    missing_check_in: string[];
    missing_checkout: string[];
    attendance_complete: boolean;
    device_sync_status: 'team_confirmation_required';
    warnings: string[];
  };
}

export interface ActivityReportResponse {
  current: ActivityReportSnapshot;
  provenance: ActivityReportProvenance;
  latest_certification: ActivityReportCertification | null;
  certification_history: ActivityReportCertification[];
}

export interface ActivitySession {
  id: string;
  reference: string;
  shift: string;
  campaign_id: string;
  campaign_name: string;
  activity_site_id: string;
  activity_site_name: string;
  city: string;
  starts_at: string;
  ends_at: string;
  lead: string;
  lead_name: string;
  backup_lead: string | null;
  backup_lead_name: string | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
  current_totals: ActivitySessionTotals;
  latest_certification: ActivityReportCertification | null;
  has_uncertified_changes: boolean;
  created_at: string;
  updated_at: string;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
  district: string;
  country: string;
  chain: string | null;
  chain_name: string;
  chain_code: string | null;
  store_code: string;
  latitude: string;
  longitude: string;
  timezone: string;
  verification_status: VenueVerificationStatus;
  source: VenueSource;
  source_reference: string;
  is_active: boolean;
  is_usable: boolean;
  merged_into: string | null;
  merged_into_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface StoreChainAlias {
  id: string;
  chain: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoreChain {
  id: string;
  name: string;
  code: string;
  source: 'legacy' | 'manual' | 'bulk_import' | 'external';
  source_reference: string;
  is_active: boolean;
  aliases: StoreChainAlias[];
  created_at: string;
  updated_at: string;
}

export type VenueVerificationStatus = 'pending' | 'verified' | 'rejected';
export type VenueSource = 'legacy' | 'manual' | 'venue_request' | 'bulk_import' | 'external';

export interface VenueMergeAudit {
  id: string;
  reference: string;
  source_venue: string;
  source_venue_name: string;
  target_venue: string;
  target_venue_name: string;
  reviewed_by_name: string;
  reason: string;
  source_snapshot: Record<string, unknown>;
  campaign_location_count: number;
  daily_sales_report_count: number;
  resolved_request_count: number;
  created_at: string;
}

export type VenueImportStatus = 'previewed' | 'confirmed';
export type VenueImportClassification =
  | 'valid'
  | 'invalid'
  | 'exact_duplicate'
  | 'possible_duplicate'
  | 'conflict';

export interface VenueImportBatch {
  id: string;
  reference: string;
  template_version: string;
  original_filename: string;
  file_sha256: string;
  file_size: number;
  status: VenueImportStatus;
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  exact_duplicate_count: number;
  possible_duplicate_count: number;
  conflict_count: number;
  imported_count: number;
  expires_at: string;
  is_expired: boolean;
  created_by_name: string;
  confirmed_by_name: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface VenueImportMatch {
  venue?: string;
  preview_row?: number;
  name?: string;
  address?: string;
  city?: string;
  reason: 'normalized_identity' | 'chain_store_code' | 'nearby_coordinates';
  distance_m?: number;
}

export interface VenueImportRow {
  id: string;
  row_number: number;
  classification: VenueImportClassification;
  data: Record<string, string>;
  errors: Record<string, string[]> | string[];
  matches: VenueImportMatch[];
  is_importable: boolean;
  created_venue: string | null;
  created_venue_name: string | null;
}

export type ActivitySiteImportBatch = VenueImportBatch;

export interface ActivitySiteImportMatch {
  activity_site?: string;
  preview_row?: number;
  name?: string;
  address?: string;
  city?: string;
  reason: 'normalized_identity' | 'nearby_coordinates';
  distance_m?: number;
}

export interface ActivitySiteImportRow {
  id: string;
  row_number: number;
  classification: VenueImportClassification;
  data: Record<string, string>;
  errors: Record<string, string[]> | string[];
  matches: ActivitySiteImportMatch[];
  is_importable: boolean;
  created_activity_site: string | null;
  created_activity_site_name: string | null;
}

export type VenueRequestStatus = 'submitted' | 'approved' | 'rejected' | 'duplicate';

export interface VenueRequest {
  id: string;
  reference: string;
  organization: string;
  organization_name: string;
  requested_by_name: string;
  name: string;
  address: string;
  city: string;
  province: string;
  district: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  chain: string | null;
  chain_name: string;
  chain_code: string | null;
  requested_chain_name: string;
  store_code: string;
  notes: string;
  status: VenueRequestStatus;
  resolved_venue: Venue | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_note: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignShift {
  id: string;
  campaign: string;
  campaign_name: string;
  location: string;
  location_name: string;
  location_city: string;
  schedule: string | null;
  schedule_name: string | null;
  schedule_rule_name: string | null;
  starts_at: string;
  ends_at: string;
  checkin_open_minutes: number;
  late_after_minutes: number;
  checkout_grace_minutes: number;
  created_at: string;
  updated_at: string;
}

export interface ShiftScheduleRule {
  id: string;
  name: string;
  weekdays: string[];
  start_time: string;
  end_time: string;
  occurrence_count: number;
}

export interface ShiftSchedule {
  id: string;
  campaign: string;
  campaign_name: string;
  group: string | null;
  supersedes: string | null;
  revision_number: number;
  revision_reason: string;
  location: string;
  location_name: string;
  location_city: string;
  timezone: string;
  name: string;
  starts_on: string;
  ends_on: string;
  checkin_open_minutes: number;
  late_after_minutes: number;
  checkout_grace_minutes: number;
  rules: ShiftScheduleRule[];
  occurrence_count: number;
  staffed_occurrence_count: number;
  has_assignment_history: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShiftScheduleRevisionImpact {
  preserved: number;
  created: number;
  cancelled: number;
  protected_conflicts: number;
}

export interface ProductFamily {
  id: string;
  client: string;
  client_name: string;
  name: string;
  brand: string | null;
  brand_name: string | null;
  is_active: boolean;
  variant_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProductBrand {
  id: string;
  client: string;
  client_name: string;
  name: string;
  theme_color: string;
  is_active: boolean;
  family_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCategory {
  id: string;
  name: string;
  is_active: boolean;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  client: string;
  client_name: string;
  family?: string | null;
  family_name?: string | null;
  family_brand?: string | null;
  name: string;
  variant_label?: string;
  sku: string;
  theme_color?: string;
  barcode: string;
  packaging_type: 'bottle' | 'can' | 'bag' | 'packet' | 'box' | 'jar' | 'tube' | 'sachet' | 'tin' | 'carton' | 'other' | '';
  units_per_case: number | null;
  category?: string | null;
  category_name?: string | null;
  image: string | null;
  unit_price: string | null;
  package_quantity: string | null;
  measurement_unit: 'piece' | 'g' | 'kg' | 'ml' | 'l' | '';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductPrice {
  id: string;
  product: string;
  product_name: string;
  product_client: string;
  venue: string | null;
  venue_name: string | null;
  price_type: 'standard' | 'promotional';
  amount: string;
  starts_on: string;
  ends_on: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type ProductImportClassification = 'valid_new' | 'valid_update' | 'invalid' | 'conflict';

export interface ProductImportBatch {
  id: string;
  reference: string;
  client: string;
  client_name: string;
  original_filename: string;
  status: 'previewed' | 'confirmed';
  total_rows: number;
  valid_new_count: number;
  valid_update_count: number;
  invalid_count: number;
  conflict_count: number;
  imported_count: number;
  expires_at: string;
  is_expired: boolean;
  created_at: string;
}

export interface ProductImportRow {
  id: string;
  row_number: number;
  classification: ProductImportClassification;
  data: { source: Record<string, string>; payload: Record<string, unknown> };
  errors: Record<string, string[]> | string[];
  matched_product: string | null;
  imported_product: string | null;
}

export interface CampaignFreeSampleDeal {
  id: string;
  campaign: string;
  qualifying_product: string;
  qualifying_product_name: string;
  qualifying_quantity: number;
  sample_quantity: number;
  sample_product_details: Array<{ id: string; name: string; sku: string; is_active: boolean }>;
  is_active: boolean;
}

export interface CampaignProduct {
  id: string;
  campaign: string;
  campaign_name: string;
  product: string;
  product_name: string;
  product_sku: string;
  product_category: string;
  product_family: string | null;
  product_family_name?: string | null;
  product_variant_label?: string;
  product_unit_price: string | null;
  product_package_quantity: string | null;
  product_measurement_unit: 'piece' | 'g' | 'kg' | 'ml' | 'l' | '';
  product_is_active: boolean;
  target_value: string | null;
  target_metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  target_period: 'campaign' | 'monthly';
  free_sample_deal?: CampaignFreeSampleDeal | null;
}

export interface CampaignProductFamilyTarget {
  id: string;
  campaign: string;
  campaign_name: string;
  product_family: string;
  product_family_name: string;
  product_brand_name: string | null;
  attached_variant_count: number;
  target_value: string;
  target_metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  target_period: 'campaign' | 'monthly';
}

export interface CampaignLocationProductTarget {
  id: string;
  location: string;
  campaign_product: string;
  campaign: string;
  campaign_name: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  product: string;
  product_name: string;
  product_sku: string;
  target_value: string;
  target_metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  target_period: 'campaign' | 'monthly';
}

export interface CampaignLocationProductFamilyTarget {
  id: string;
  location: string;
  campaign_product_family_target: string;
  campaign: string;
  campaign_name: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  product_family: string;
  product_family_name: string;
  product_brand_name: string | null;
  target_value: string;
  target_metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  target_period: 'campaign' | 'monthly';
}

export interface StockReceipt {
  id: string;
  reference: string;
  campaign_location: string;
  campaign: string;
  campaign_name: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  campaign_product: string;
  product: string;
  product_name: string;
  product_sku: string;
  receiving_organization: string;
  receiving_organization_name: string;
  quantity_received: number;
  received_at: string;
  delivery_reference: string;
  note: string;
  recorded_by: string;
  recorded_by_name: string;
  created_at: string;
}

export interface StoreProductAvailability {
  id: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  product: string;
  product_name: string;
  product_sku: string;
  is_out_of_stock: boolean;
  changed_by: string;
  changed_by_name: string;
  updated_at: string;
}

export interface PhysicalStockCountLine {
  id: string;
  campaign_product: string;
  quantity_units: number;
  product_name_snapshot: string;
  product_sku_snapshot: string;
  product_family_snapshot: string;
  package_quantity_snapshot: string | null;
  measurement_unit_snapshot: string;
  normalized_quantity: string;
  normalized_unit: string;
}

export interface PhysicalStockCountReviewDecision {
  id: string;
  decision: 'approved' | 'rejected';
  note: string;
  reviewer: string;
  reviewer_name: string;
  reviewer_organization: string | null;
  reviewer_organization_name: string | null;
  created_at: string;
}

export interface PhysicalStockCount {
  id: string;
  reference: string;
  visit: string;
  visit_reference: string;
  campaign: string;
  campaign_name: string;
  campaign_location: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  organization: string;
  organization_name: string;
  supervisor: string;
  supervisor_name: string;
  captured_at: string;
  note: string;
  lines: PhysicalStockCountLine[];
  photos: Array<{ id: string; photo: string; created_at: string }>;
  review_status: 'pending' | 'approved' | 'rejected';
  can_decide: boolean;
  review_decisions: PhysicalStockCountReviewDecision[];
  created_at: string;
}

export interface CurrentPhysicalStockPosition extends PhysicalStockCountLine {
  stock_count: string;
  stock_count_reference: string;
  captured_at: string;
  campaign: string;
  campaign_name: string;
  campaign_location: string;
  venue_name: string;
  venue_city: string;
  supervisor_name: string;
}

export interface CurrentBAShelfStockPosition extends PhysicalStockCountLine {
  observation: string;
  observation_reference: string;
  captured_at: string;
  campaign: string;
  campaign_name: string;
  campaign_location: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_name: string;
}

export interface Assignment {
  id: string;
  reference: string;
  supersedes: string | null;
  campaign: string;
  campaign_name: string;
  owner_organization_id: string;
  client_id: string;
  operating_agency_id: string | null;
  schedule: {
    id: string;
    name: string;
    starts_on: string;
    ends_on: string;
    location_name: string;
    location_city: string;
    occurrence_count: number;
  } | null;
  shift: {
    id: string;
    starts_at: string;
    ends_at: string;
    checkin_open_minutes: number;
    late_after_minutes: number;
    checkout_grace_minutes: number;
    status: 'active' | 'cancelled';
    location: {
      id: string;
      site_type?: 'store' | ActivitySiteKind;
      name: string;
      city: string;
      address: string;
      latitude: string;
      longitude: string;
      allowed_radius_m: number;
      timezone: string;
    };
  };
  brand_ambassador: string;
  brand_ambassador_name: string;
  brand_ambassador_reference: string;
  hired_by: string;
  hired_by_name: string;
  status: 'offered' | 'accepted' | 'declined' | 'cancelled' | 'completed';
  attendance_state: 'not_checked_in' | 'checked_in' | 'checked_out' | 'checkout_missing';
  missed_checkout: {
    event_id: string;
    event_reference: string;
    checked_in_at: string;
    shift: Assignment['shift'];
  } | null;
  responded_at: string | null;
}

export interface BaAttendanceEvent {
  id: string;
  reference: string;
  assignment_id: string;
  campaign_name: string;
  venue_name: string;
  shift_starts_at: string;
  shift_ends_at: string;
  event_type: 'check_in' | 'check_out';
  captured_at: string;
  received_at: string;
  latitude: string;
  longitude: string;
  horizontal_accuracy_m: string;
  distance_from_site_m: string;
  validation_status: 'accepted' | 'flagged' | 'rejected';
  validation_reason: string;
  display_validation_reason?: string;
}

export interface BaCampaignProduct {
  id: string;
  campaign: string;
  campaign_name: string;
  product: string;
  product_name: string;
  product_sku: string;
  product_category: string;
  product_unit_price: string | null;
  product_is_active: boolean;
  product_package_quantity: string | null;
  product_measurement_unit: 'piece' | 'g' | 'kg' | 'ml' | 'l' | '';
  store_out_of_stock?: boolean;
  target_value: string | null;
  target_metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  free_sample_deal?: CampaignFreeSampleDeal | null;
}

export interface SalesSampleLine {
  id: string;
  deal_id: string;
  qualifying_product_id: string;
  qualifying_product_name: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  quantity: number;
  qualifying_quantity_snapshot?: number;
  sample_quantity_snapshot?: number;
}

export interface BaSalesSubmission {
  id: string;
  reference: string;
  assignment_id: string;
  assignment_reference: string;
  campaign_name: string;
  venue_name: string;
  sold_at: string;
  submission_type: 'sales' | 'no_sales';
  no_sales_reason: 'no_customer_demand' | 'out_of_stock' | 'store_closed' | 'product_unavailable' | 'other' | '';
  status: 'submitted' | 'corrected' | 'voided';
  notes: string;
  total_units: number;
  total_value: string | null;
  lines: Array<{
    id: string;
    product_id: string;
    product_name: string;
    product_sku: string;
    quantity: number;
    unit_price: string | null;
    line_total: string | null;
  }>;
  samples: SalesSampleLine[];
  created_at: string;
}

export interface BaTargetProgress {
  metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  display_unit: string;
  target_value: string;
  achieved_value: string;
  percentage: string;
}

export interface BaSelfPerformanceResponse {
  profile_reference: string;
  period: { date_from: string; date_to: string };
  performance: {
    id: string;
    name: string;
    avatar: string | null;
    campaign_count: number;
    assignment_offers: number;
    assignment_accepted: number;
    assignment_declined: number;
    assignment_completed: number;
    assignment_acceptance_percentage: string;
    assignment_completion_percentage: string;
    scheduled_shifts: number;
    attended_shifts: number;
    attendance_compliance_percentage: string;
    approved_check_ins: number;
    on_time_check_ins: number;
    late_check_ins: number;
    punctuality_percentage: string;
    flagged_events: number;
    rejected_events: number;
    approved_sales_units: number;
    approved_sales_value: string | null;
    target_progress: BaTargetProgress[];
    target_attainment_percentage: string | null;
  };
  privacy_notice: string;
}

export interface AttendanceReviewEvent {
  id: string;
  reference: string;
  assignment_id: string;
  assignment_reference: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_id: string;
  brand_ambassador_name: string;
  hired_by_id: string;
  hired_by_name: string;
  shift_starts_at: string;
  shift_ends_at: string;
  shift_checkin_open_minutes: number;
  shift_checkout_grace_minutes: number;
  event_type: 'check_in' | 'check_out';
  captured_at: string;
  received_at: string;
  latitude: string;
  longitude: string;
  horizontal_accuracy_m: string;
  distance_from_site_m: string;
  location_captured_at: string | null;
  location_mocked: boolean | null;
  location_sample_count: number | null;
  location_max_drift_m: string | null;
  selfie: string;
  validation_status: 'accepted' | 'flagged' | 'rejected';
  validation_reason: string;
  display_validation_reason?: string;
  app_version: string;
  device_time_offset_minutes: number | null;
  review_status: 'pending' | 'approved' | 'rejected';
  review_source: 'automatic' | 'pending' | 'human';
  can_decide: boolean;
  review_decisions: AttendanceReviewDecision[];
  checkout_missing: boolean;
  can_resolve_missing_checkout: boolean;
  checkout_resolution: MissedCheckoutResolution | null;
}

export interface MissedCheckoutResolution {
  id: string;
  reference: string;
  effective_checkout_at: string;
  note: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_organization_id: string | null;
  reviewer_organization_name: string | null;
  created_at: string;
}

export interface AttendanceReviewDecision {
  id: string;
  decision: 'approved' | 'rejected';
  note: string;
  is_override: boolean;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_organization_id: string | null;
  reviewer_organization_name: string | null;
  created_at: string;
}

export interface AttendanceReviewResponse extends PaginatedResponse<AttendanceReviewEvent> {
  evaluated_at: string;
  summary: {
    total: number;
    accepted: number;
    flagged: number;
    rejected: number;
    review_pending: number;
    review_approved: number;
    review_rejected: number;
    checkout_missing: number;
  };
}

export interface AttendancePendingEvidence {
  id: string;
  assignment_id: string;
  assignment_reference: string;
  campaign_id: string;
  campaign_name: string;
  brand_ambassador_id: string;
  brand_ambassador_name: string;
  shift_id: string;
  shift_starts_at: string;
  shift_ends_at: string;
  campaign_location_id: string;
  site_type: string;
  venue_name: string;
  venue_city: string;
  event_type: 'check_in' | 'check_out';
  captured_at: string;
  received_at: string;
  expires_at: string;
  status: 'evidence_pending';
}

export type OperationalExceptionType =
  | 'no_effective_check_in'
  | 'attendance_complete_missing_sales'
  | 'sales_without_effective_attendance';

export interface OperationalException {
  exception_type: OperationalExceptionType;
  assignment_id: string;
  assignment_reference: string;
  shift_id: string;
  shift_starts_at: string;
  shift_ends_at: string;
  brand_ambassador_id: string;
  brand_ambassador_name: string;
  brand_ambassador_reference: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  has_effective_check_in: boolean;
  check_in_submission_status: 'no_attempt_received' | 'evidence_pending' | 'attendance_recorded' | 'attempt_expired';
  has_effective_check_out: boolean;
  has_sales_submission: boolean;
}

export interface OperationalExceptionResponse extends PaginatedResponse<OperationalException> {
  evaluated_at: string;
}

export interface ShelfEvidenceDecision {
  id: string;
  decision: 'approved' | 'rejected';
  note: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_organization_id: string | null;
  reviewer_organization_name: string | null;
  created_at: string;
}

export interface ShelfEvidenceSubmission {
  id: string;
  reference: string;
  assignment_id: string;
  assignment_reference: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  shift_id: string;
  shift_starts_at: string;
  shift_ends_at: string;
  brand_ambassador_id: string;
  brand_ambassador_name: string;
  hired_by_id: string;
  hired_by_name: string;
  products: Array<{ id: string; name: string; sku: string }>;
  inventory_counts: Array<{
    id: string;
    product_id: string;
    product_name: string;
    product_sku: string;
    available_units: number;
  }>;
  photo: string;
  notes: string;
  captured_at: string;
  received_at: string;
  latitude: string;
  longitude: string;
  horizontal_accuracy_m: string;
  distance_from_site_m: string;
  location_captured_at: string | null;
  location_mocked: boolean | null;
  location_sample_count: number | null;
  location_max_drift_m: string | null;
  app_version: string;
  review_status: 'pending' | 'approved' | 'rejected';
  can_decide: boolean;
  review_decisions: ShelfEvidenceDecision[];
}

export interface SurveyVisit {
  id: string;
  reference: string;
  campaign_id: string;
  campaign_name: string;
  client_id: string;
  client_name: string;
  template_name: string;
  template_version: number;
  brand_ambassador_id: string;
  brand_ambassador_name: string;
  outlet_source: 'catalogue' | 'discovered' | 'activity_session';
  outlet_name_snapshot: string;
  outlet_city_snapshot: string;
  outlet_area_snapshot: string;
  outlet_address_snapshot: string;
  business_date: string;
  captured_at: string;
  received_at: string;
  latitude: string | null;
  longitude: string | null;
  horizontal_accuracy_m: string | null;
  location_captured_at: string | null;
  location_mocked: boolean | null;
  location_sample_count: number | null;
  location_max_drift_m: string | null;
  integrity_flags: string[];
  status: 'submitted';
  app_version: string;
  collection_source: 'field_app' | 'manual';
  manually_entered_by_name: string | null;
  manual_entry_reason: string;
  manual_import_id: string | null;
  storefront_photo: string | null;
}

export interface SurveyVisitDetail extends SurveyVisit {
  outlet_owner_name: string;
  outlet_owner_contact: string;
  photos: Array<{ id: string; kind: 'storefront'; photo: string; captured_at: string }>;
  answers: Array<{
    question_id: string;
    question_key_snapshot: string;
    question: SurveyQuestionDefinition;
    value: unknown;
  }>;
  questions: SurveyQuestionDefinition[];
}

export interface SurveyOutletReview {
  id: string;
  name: string;
  city: string;
  area: string;
  address: string;
  visit_count: number;
  latest_visit_at: string | null;
}

export type SurveyQuestionKind =
  | 'yes_no'
  | 'single_select'
  | 'multi_select'
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'phone'
  | 'photo'
  | 'location';

export interface SurveyQuestionDefinition {
  id: string;
  key: string;
  section: string;
  label: string;
  help_text: string;
  kind: SurveyQuestionKind;
  required: boolean;
  position: number;
  choices: Array<{ value: string; label: string }>;
  visibility_condition: Record<string, unknown>;
}

export interface SurveyTemplate {
  id: string;
  client: string;
  client_name: string;
  brand: string | null;
  brand_name: string | null;
  name: string;
  slug: string;
  description: string;
  reporting_preset: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type SurveyReportChartType = 'bar' | 'pie' | 'donut';

export interface SurveyChartColors {
  coverage_by_city: string;
  question_default: string;
  availability_by_city: Record<string, string>;
  availability_sections: Record<string, string>;
  question_options: Record<string, string>;
}

export interface SurveyReportingPreset {
  id: string;
  key: string;
  name: string;
  description: string;
  availability_chart_type: SurveyReportChartType;
  question_chart_type: SurveyReportChartType;
  reporting_groups: Array<{
    key: string;
    label: string;
    columns: Array<{
      key: string;
      label: string;
      questions: Array<{ key: string; label: string }>;
    }>;
  }>;
  availability_sections: Array<{
    key: string;
    label: string;
    aggregation: 'any_true' | 'true' | 'answered';
    question_keys: string[];
  }>;
  chart_colors: SurveyChartColors;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SurveyTemplateVersion {
  id: string;
  template: string;
  template_name: string;
  client: string;
  client_name: string;
  brand: string | null;
  brand_name: string | null;
  version: number;
  status: 'draft' | 'published';
  notes: string;
  published_at: string | null;
  questions: SurveyQuestionDefinition[];
  created_at: string;
}

export interface CampaignSurvey {
  id: string;
  campaign: string;
  campaign_name: string;
  client: string;
  client_name: string;
  template_version: string;
  template: string;
  template_name: string;
  template_brand: string | null;
  template_brand_name: string | null;
  template_version_number: number;
  starts_on: string;
  ends_on: string;
  follows_campaign_start: boolean;
  follows_campaign_end: boolean;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  territory_label: string;
  settings: Record<string, unknown>;
  fieldwork_plans: Array<{
    id: string;
    campaign_survey: string;
    city: string;
    area: string;
    planned_working_days: number;
    visit_quota: number | null;
    created_at: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface PakistanCity {
  id: string;
  name: string;
  province: string;
}

export interface SurveyCampaignAssignment {
  id: string;
  campaign_survey: string;
  campaign: string;
  campaign_name: string;
  brand_ambassador: string;
  brand_ambassador_name: string;
  starts_on: string;
  ends_on: string;
  allowed_cities: string[];
  allowed_areas: string[];
  status: 'active' | 'cancelled';
  cancelled_at: string | null;
  cancellation_reason: string;
  created_at: string;
}

export interface ManualSurveyAssignment extends SurveyCampaignAssignment {
  template_version: string;
  template_name: string;
  template_version_number: number;
  questions: SurveyQuestionDefinition[];
  catalogue_stores: Array<{
    id: string;
    name: string;
    city: string;
    address: string;
  }>;
}

export interface ManualSurveyImportPreview {
  preview_token: string;
  import_id: string;
  valid_count: number;
  invalid_count: number;
  rows: Array<{
    row_number: number;
    status: 'valid' | 'invalid';
    detail: string;
    values?: Record<string, string>;
  }>;
}

export interface SurveyLegacyMetrics {
  premium_comfort_presence: number;
  savers_presence: number;
  known_competitor_category_presence: number;
  any_competitor_observed: number;
  always_available: number;
  butterfly_available: number;
  molped_available: number;
  nofea_available: number;
  other_brand_observed: number;
  premium_comfort_availability_index: number | null;
  premium_comfort_vs_always_index: number | null;
  savers_availability_index: number | null;
  savers_vs_butterfly_index: number | null;
}

export interface SurveyDashboardCity extends SurveyLegacyMetrics {
  city: string;
  visits: number;
  distinct_outlets: number;
  planned_working_days: number;
  days_complete: number;
  visit_quota: number;
}

export interface SurveyQuestionSummary {
  id: string;
  key: string;
  label: string;
  section: string;
  kind: string;
  answer_count: number;
  options: Array<{ value: string; label: string; count: number }>;
}

export interface SurveyAvailabilityChartSection {
  key: string;
  label: string;
  count: number;
}

export interface SurveyReportingGroup {
  key: string;
  label: string;
  columns: Array<{
    key: string;
    label: string;
    questions: Array<{
      key: string;
      label: string;
      kind: string;
      answer_count: number;
      options: Array<{ value: string; label: string; count: number }>;
    }>;
  }>;
}

export interface SurveyDashboardResponse {
  generated_at: string;
  summary: SurveyLegacyMetrics & {
    visits: number;
    distinct_outlets: number;
    planned_working_days: number;
    days_complete: number;
    visit_quota: number;
  };
  cities: SurveyDashboardCity[];
  question_summaries: SurveyQuestionSummary[];
  availability_chart: {
    sections: SurveyAvailabilityChartSection[];
    cities: Array<{ city: string; sections: SurveyAvailabilityChartSection[] }>;
  };
  chart_configuration: {
    availability_mix: SurveyReportChartType;
    question_distribution: SurveyReportChartType;
    colors: SurveyChartColors;
  };
  reporting_groups: SurveyReportingGroup[];
  is_embrace_compatible: boolean;
}

export interface SurveyReportingOptions {
  campaigns: Array<{ id: string; name: string }>;
  template_versions: Array<{ id: string; name: string; version: number }>;
  brand_ambassadors: Array<{ id: string; name: string }>;
  cities: string[];
}

export interface SalesReviewLine {
  id: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: string | null;
  line_total: string | null;
  price_breakdown?: Array<{
    unit_price: string;
    quantity: number;
  }>;
}

export interface SalesReviewDecision {
  id: string;
  decision: 'approved' | 'rejected' | 'corrected';
  corrected_no_sales_reason: 'no_customer_demand' | 'out_of_stock' | 'store_closed' | 'product_unavailable' | 'other' | '';
  note: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_organization_id: string | null;
  reviewer_organization_name: string | null;
  corrected_lines: SalesReviewLine[];
  corrected_samples?: SalesSampleLine[];
  has_sample_correction?: boolean;
  created_at: string;
}

export interface SalesReviewSubmission {
  id: string;
  reference: string;
  campaign_id: string;
  campaign_location_id?: string | null;
  campaign_name: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_id: string | null;
  brand_ambassador_name: string;
  reviewer_organization_id: string;
  reviewer_organization_name: string;
  business_date: string;
  submitted_at: string;
  created_at: string;
  source_count: number;
  source_submissions: Array<{
    id: string;
    reference: string;
    assignment_id: string | null;
    assignment_reference: string | null;
    shift_id: string | null;
    shift_starts_at: string | null;
    shift_ends_at: string | null;
    collection_source?: 'field_app' | 'manual';
    entered_by_name?: string;
    manual_entry_reason?: string;
    import_reference?: string;
    sold_at: string;
    submission_type: 'sales' | 'no_sales';
    no_sales_reason: 'no_customer_demand' | 'out_of_stock' | 'store_closed' | 'product_unavailable' | 'other' | '';
    notes: string;
    total_units: number;
    total_value: string | null;
    lines: SalesReviewLine[];
    samples?: SalesSampleLine[];
    created_at: string;
  }>;
  lines: SalesReviewLine[];
  original_total_units: number;
  original_total_value: string | null;
  effective_lines: SalesReviewLine[];
  effective_samples?: SalesSampleLine[];
  effective_total_units: number;
  effective_total_value: string | null;
  review_status: 'pending' | 'pending_changes' | 'approved' | 'rejected' | 'corrected';
  has_new_submissions: boolean;
  can_decide: boolean;
  review_decisions: SalesReviewDecision[];
}

export interface SalesReviewResponse extends PaginatedResponse<SalesReviewSubmission> {
  summary: {
    total: number;
    pending: number;
    approved: number;
    corrected: number;
    rejected: number;
    accepted_units: number;
    accepted_value: string;
  };
}

export interface SalesDailySummary {
  business_date: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_id: string | null;
  brand_ambassador_name: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  report_count: number;
  pending_reports: number;
  accepted_reports: number;
  original_units: number;
  original_value: string | null;
  accepted_units: number;
  accepted_value: string | null;
}

export interface SalesBulkApprovalResponse {
  approved_count: number;
  decisions: SalesReviewDecision[];
}

export interface DashboardFilterOption {
  id: string;
  name: string;
}

export interface DashboardAttendanceItem {
  id: string;
  reference: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string | null;
  activity_site_id: string | null;
  site_type: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_name: string;
  event_type: 'check_in' | 'check_out';
  captured_at: string;
  validation_reason: string;
}

export interface DashboardSalesItem {
  id: string;
  reference: string;
  campaign_id: string;
  campaign_name: string;
  venue_id: string | null;
  activity_site_id: string | null;
  site_type: string;
  venue_name: string;
  venue_city: string;
  brand_ambassador_name: string;
  sold_at: string;
  total_units: number;
  total_value: string | null;
}

export interface DashboardCharts {
  sales_trend: Array<{
    date: string;
    units: number;
    value: string;
  }>;
  daily_ba_presence: Array<{
    date: string;
    count: number;
  }>;
  target_attainment: {
    target_count: number;
    percentage: string;
    summaries: TargetProgress[];
  };
  attendance_compliance: {
    approved: number;
    pending: number;
    rejected: number;
    total: number;
    percentage: string;
  };
  campaign_performance: Array<{
    id: string;
    name: string;
    percentage: string;
    summaries: TargetProgress[];
  }>;
  venue_comparison: Array<{
    id: string;
    name: string;
    city: string;
    accepted_units: number;
    accepted_value: string;
    accepted_mass_kilograms: string;
    accepted_volume_litres: string;
    target_volume_litres: string;
    volume_percentage: string;
    percentage: string;
    summaries: TargetProgress[];
    brand_breakdown: Array<{
      brand_id: string | null;
      brand_name: string;
      brand_theme_color: string | null;
      accepted_units: number;
      accepted_value: string;
      accepted_mass_kilograms: string;
      accepted_volume_litres: string;
      target_volume_litres: string;
      volume_percentage: string;
      percentage: string;
      target_percentage_contribution: string;
      summaries: TargetProgress[];
    }>;
  }>;
  product_breakdown: Array<{
    id: string;
    name: string;
    sku: string;
    brand_theme_color: string | null;
    product_theme_color?: string | null;
    accepted_units: number;
    accepted_value: string;
  }>;
  brand_family_breakdown: Array<{
    client_id: string;
    client_name: string;
    client_short_name: string;
    brand_id: string | null;
    brand_name: string;
    brand_theme_color: string | null;
    family_id: string | null;
    family_name: string;
    accepted_units: number;
    accepted_value: string;
  }>;
}

export interface TargetProgress {
  metric: 'pieces' | 'mass' | 'volume' | 'revenue';
  display_unit: 'pieces' | 'kg' | 'L' | 'PKR';
  target_value: string;
  achieved_value: string;
  percentage: string;
}

export interface DashboardOverview {
  period: {
    date_from: string;
    date_to: string;
  };
  summary: {
    campaigns_total: number;
    campaigns_active: number;
    active_assignments: number;
    attendance_events: number;
    attendance_pending: number;
    sales_reports: number;
    sales_pending: number;
    sales_accepted_units: number;
    sales_accepted_value: string;
  };
  charts: DashboardCharts;
  attendance_attention: DashboardAttendanceItem[];
  sales_attention: DashboardSalesItem[];
  filters: {
    clients: DashboardFilterOption[];
    brands: DashboardFilterOption[];
    agencies: DashboardFilterOption[];
    campaigns: DashboardFilterOption[];
    venues: DashboardFilterOption[];
  };
}

export interface DashboardStoreDetail {
  id: string;
  name: string;
  city: string;
  target_attainment: {
    target_count: number;
    percentage: string;
    summaries: TargetProgress[];
  };
  contributors: Array<{
    id: string;
    name: string;
    accepted_units: number;
    accepted_value: string;
  }>;
  dates: string[];
  products: Array<{
    id: string;
    name: string;
    sku: string;
    brand_name: string;
    brand_theme_color: string | null;
    product_theme_color?: string | null;
    daily_sales: Array<{
      date: string;
      accepted_units: number;
      accepted_value: string;
    }>;
  }>;
}

export interface DashboardDailyBAPresenceDetail {
  date: string;
  stores: Array<{
    id: string;
    name: string;
    latitude: string;
    longitude: string;
    status: 'present' | 'scheduled_no_effective_checkin' | 'not_scheduled';
    brand_ambassadors: Array<{
      id: string;
      name: string;
    }>;
  }>;
}

export interface DashboardProductDetail {
  id: string;
  name: string;
  sku: string;
  brand_name: string;
  brand_theme_color: string | null;
  product_theme_color?: string | null;
  target_attainment: {
    target_count: number;
    percentage: string;
    summaries: TargetProgress[];
  };
  contributors: Array<{
    id: string;
    name: string;
    accepted_units: number;
    accepted_value: string;
  }>;
  dates: string[];
  stores: Array<{
    id: string;
    name: string;
    city: string;
    daily_sales: Array<{
      date: string;
      accepted_units: number;
      accepted_value: string;
    }>;
  }>;
}

export interface BrandAmbassadorPerformance {
  id: string;
  name: string;
  avatar: string | null;
  campaign_count: number;
  assignment_offers: number;
  assignment_accepted: number;
  assignment_declined: number;
  assignment_completed: number;
  assignment_acceptance_percentage: string;
  assignment_completion_percentage: string;
  scheduled_shifts: number;
  attended_shifts: number;
  attendance_compliance_percentage: string;
  approved_check_ins: number;
  on_time_check_ins: number;
  late_check_ins: number;
  punctuality_percentage: string;
  flagged_events: number;
  rejected_events: number;
  approved_sales_units: number;
  approved_sales_value: string | null;
  target_progress: TargetProgress[];
  target_attainment_percentage: string | null;
}

export interface PerformancePage extends PaginatedResponse<BrandAmbassadorPerformance> {
  period: { date_from: string; date_to: string };
  summary: {
    ambassadors: number;
    scheduled_shifts: number;
    attended_shifts: number;
    flagged_events: number;
    rejected_events: number;
    approved_sales_units: number;
    approved_sales_value: string | null;
  };
  filters: {
    clients: DashboardFilterOption[];
    agencies: DashboardFilterOption[];
    campaigns: DashboardFilterOption[];
    venues: DashboardFilterOption[];
    brand_ambassadors: DashboardFilterOption[];
  };
}
